import { useAuth0 } from '@auth0/auth0-react';
import type { UserMetadata } from '@generated/gql';
import { useCallback, useMemo } from 'react';

const DEFAULT_USER_METADATA = {
  firstName: '',
  lastName: '',
  timeZone: 'UTC',
  isLocalTime: false,
  phoneNumber: '',
  notificationPreference: '',
  isOrganisationSelected: false,
  org_id: undefined,
};

function isUserMetaData(data: Record<string, any>): data is UserMetadata & typeof DEFAULT_USER_METADATA {
  return typeof data.firstName === 'string' && typeof data.lastName === 'string';
}

const setTimeZone = (timeZone: string | null | undefined) => {
  if (!timeZone) {
    return { isLocalTime: false, timeZone: 'UTC' };
  }
  if (timeZone.toLocaleLowerCase() === 'local') {
    return {
      isLocalTime: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }
  return { isLocalTime: false, timeZone };
};

export const useUserMetadata = () => {
  const { user, isLoading, getAccessTokenSilently } = useAuth0();
  const userMetadata = useMemo(() => {
    if (!user) {
      return { ...DEFAULT_USER_METADATA, ...setTimeZone(null) };
    }

    const auth0Audience = import.meta.env.PUBLIC_AUTH0_AUDIENCE;
    const data: Record<string, any> = user[`${auth0Audience}user_metadata`] || {};
    const timeZoneData = setTimeZone(data.timeZone);

    if (isUserMetaData(data)) {
      return {
        ...DEFAULT_USER_METADATA,
        ...data,
        ...timeZoneData,
        ...user,
      };
    }

    return { ...DEFAULT_USER_METADATA, ...timeZoneData };
  }, [user]);

  const refreshUserData = useCallback(async () => {
    await getAccessTokenSilently({
      cacheMode: 'off',
    });
  }, []);

  return { userMetadata, isLoading, refreshUserData };
};
